// Import builderFetch
import { builderFetch } from '../../utils';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

/**
 * Updates a specific setting for a recipe via an API request and triggers a custom event on success.
 *
 * @async
 * @function setRecipeSetting
 * @param {String} settingID - The ID of the setting to update.
 * @param {Object} fields - The data to update the setting with.
 * @return {Promise<Object>} The response from the API request.
 *
 * @example
 * // Update a recipe setting and handle the response
 * setRecipeSetting( 'recipe_total_times', {
 *   "TIMES": {
 *     "type": "int",
 *     "value": "1",
 *     "backup": {
 *       "label": "Times",
 *       "show_label_in_sentence": true
 *     }
 *   }
 * })
 *   .then(response => {
 *     if (response.success) {
 *       console.log('Setting updated successfully');
 *     }
 *   });
 *
 * @type {Object}
 * @property {String} settingID - The ID of the updated setting.
 * @property {Object} fields - The data used to update the setting.
 */
const setRecipeSetting = async ( settingID, fields ) => {
	// Event name
	const eventName = 'uap/builder/core/set-recipe-setting';

	// Save the recipe title
	return builderFetch( {
		eventName: eventName,
		endpoint: 'recipe_update_setting',
		method: 'POST',
		data: {
			// eslint-disable-next-line camelcase
			recipe_id: UncannyAutomator._recipe.recipe_id,
			// eslint-disable-next-line camelcase
			setting_id: settingID,
			fields: fields
		},
		snackbar: {
			loading: escapeHTML( __( 'Updating setting', 'uncanny-automator' ) ),
			success: escapeHTML( __( 'Setting updated successfully', 'uncanny-automator' ) ),
		}
	} ).then( response => {
		// Check if the call was successful
		if ( response.success ) {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( eventName, {
				detail: {
					settingID: settingID,
					fields: fields
				}
			} ) );
		}

		return response;
	} );
}

export default setRecipeSetting;
